import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, ArrowRight, ArrowLeft, Search, BookOpen, Sparkles, Filter } from 'lucide-react';
import Navigation from './Navigation';
import SEOHead from './SEOHead';
import Footer from './Footer';

const categories = [
  {
    id: 'core',
    title: 'Core Financial Topics',
    description: 'Foundational strategies for long-term wealth accumulation and asset protection.',
    posts: [
      { title: '5 Essential Steps to Build Long-Term Wealth', slug: '5-essential-steps-to-build-longterm-wealth', date: 'Available', excerpt: 'Discover the 5 foundational pillars to compound wealth tax-free, mitigate market risks, and safeguard your legacy.' },
      { title: 'Why I Built Grow And Stay Rich: Stories That Taught Me the Real Meaning of Wealth', slug: 'why-i-built-grow-and-stay-rich-stories-that-taught-me-the-real-meaning-of-wealth', date: 'Available', excerpt: 'Personal insights and real-world experiences that inspired the creation of Grow And Stay Rich.' },
      { title: 'Conquer Your Financial Freedom: Fundamental Money Truths', slug: 'money-truths-to-build-wealth', date: 'Available', excerpt: 'Uncover fundamental money truths, IRC Sec. 7702 benefits, and zero downside market protection formulas.' },
      { title: 'Retirement Planning Mistakes to Avoid', slug: 'retirement-planning-mistakes', date: 'Planned', excerpt: 'Avoid critical tax traps and market volatility mistakes when architecting your retirement distribution timeline.' },
      { title: 'Tax Strategies for High Earners', slug: 'tax-strategies-high-earners', date: 'Planned', excerpt: 'Strategic capital structuring methods designed to keep more of your hard-earned income.' },
      { title: "How to Protect Your Family's Financial Future", slug: 'protect-family-financial-future', date: 'Planned', excerpt: 'Comprehensive living benefits coverage and generational estate preservation strategies.' }
    ]
  },
  {
    id: 'education',
    title: 'Educational Guides & Literacy',
    description: 'Comprehensive guides and explainers to elevate your financial decision making.',
    posts: [
      { title: 'Understanding Life Insurance vs. Investments', slug: 'life-insurance-vs-investments', date: 'Planned', excerpt: 'A clear comparison between equity volatility and tax-advantaged cash value accumulation.' },
      { title: 'The Complete Guide to Financial Goal Setting', slug: 'financial-goal-setting-guide', date: 'Planned', excerpt: 'A step-by-step roadmap to define, track, and achieve your family\'s financial milestones.' },
      { title: 'Why Every Family Needs an Emergency Fund', slug: 'family-emergency-fund', date: 'Planned', excerpt: 'Liquidity strategies that protect your long-term investments during unexpected life events.' },
      { title: "Why Extra Mortgage Payments Aren't Always the Smartest Move", slug: 'why-extra-mortgage-payments-arent-always-the-smartest-move', date: 'Planned', excerpt: 'Understanding opportunity cost, inflation, and tax-advantaged arbitrage strategies.' }
    ]
  },
  {
    id: 'current',
    title: 'Current Events & Strategy',
    description: 'Timely economic insights aligned with the annual financial calendar.',
    posts: [
      { title: 'Year-End Tax Planning Strategies', slug: 'year-end-tax-planning', date: 'Planned', excerpt: 'Essential year-end maneuvers to optimize tax deductions and maximize retirement contributions.' },
      { title: 'New Year Financial Resolutions That Work', slug: 'new-year-financial-resolutions', date: 'Planned', excerpt: 'Actionable financial habits engineered for long-term consistency and wealth retention.' }
    ]
  }
];

export default function BlogList() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');

  const goToHome = () => {
    navigate('/');
  };

  // Filter posts based on search query and selected category tab
  const filteredCategories = categories.map(cat => {
    if (selectedCategory !== 'all' && cat.id !== selectedCategory) {
      return null;
    }
    const matchingPosts = cat.posts.filter(post =>
      post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      post.excerpt.toLowerCase().includes(searchQuery.toLowerCase())
    );
    return matchingPosts.length > 0 ? { ...cat, posts: matchingPosts } : null;
  }).filter(Boolean);

  return (
    <div className="min-h-screen bg-[#070E1B] text-white font-opensans flex flex-col justify-between">
      <SEOHead 
        title="Financial Insights & Wealth Education Blog | Grow & Stay Rich"
        description="Read expert articles on wealth preservation, IRC Sec. 7702 tax strategies, Living Benefits, and long-term financial freedom."
        path="/blog"
      />
      
      <div>
        <Navigation />
        
        {/* Hero Header Section */}
        <div className="relative py-20 pt-28 bg-[#070E1B] text-white overflow-hidden border-b border-[#C9A84C]/25">
          {/* Background Ambient Lighting */}
          <div className="absolute inset-0 pointer-events-none overflow-hidden z-0">
            <div className="absolute -top-32 left-1/4 w-[500px] h-[500px] bg-[#C9A84C]/15 rounded-full blur-[140px] animate-pulse-glow" />
            <div className="absolute top-1/3 -right-32 w-[600px] h-[600px] bg-[#3D6ACC]/20 rounded-full blur-[160px]" />
            <div
              className="absolute inset-0 opacity-[0.06]"
              style={{
                backgroundImage: `radial-gradient(rgba(255, 255, 255, 0.4) 1px, transparent 1px)`,
                backgroundSize: '32px 32px'
              }}
            />
          </div>

          <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
              
              <button
                onClick={goToHome}
                className="inline-flex items-center text-xs font-semibold text-[#E8D4A0] hover:text-white bg-[#162236] border border-[#C9A84C]/30 px-3.5 py-1.5 rounded-full mb-6 transition-all group focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C9A84C]"
              >
                <ArrowLeft className="h-3.5 w-3.5 mr-2 group-hover:-translate-x-1 transition-transform text-[#C9A84C]" />
                Back to Home
              </button>

              <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#162236] border border-[#C9A84C]/30 text-xs font-semibold text-[#E8D4A0] tracking-wider uppercase mb-4 ml-3">
                <BookOpen className="w-3.5 h-3.5 text-[#C9A84C]" />
                Knowledge & Masterclass Hub
              </div>

              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold font-playfair tracking-tight mb-4">
                Financial Insights & <span className="text-gradient-gold">Wealth Education</span>
              </h1>
              <p className="text-lg text-slate-300 max-w-2xl font-opensans leading-relaxed">
                Expert perspectives on IRC Sec. 7702 tax-free growth, 0% downside market protection, Living Benefits, and legacy planning.
              </p>

              {/* Search Bar & Category Filter Bar */}
              <div className="mt-8 pt-6 border-t border-slate-800/80 flex flex-col md:flex-row gap-4 justify-between items-stretch md:items-center">
                
                {/* Search Input */}
                <div className="relative flex-1 max-w-md">
                  <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-[#C9A84C]" />
                  <input
                    type="text"
                    placeholder="Search articles by topic..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-2.5 bg-[#162236]/90 border border-[#C9A84C]/30 rounded-xl text-white placeholder-slate-400 focus:outline-none focus:border-[#C9A84C] text-xs sm:text-sm shadow-inner"
                  />
                </div>

                {/* Filter Tabs */}
                <div className="flex items-center gap-2 overflow-x-auto pb-2 md:pb-0 scrollbar-none">
                  <button
                    onClick={() => setSelectedCategory('all')}
                    className={`px-3.5 py-2 rounded-xl text-xs font-semibold whitespace-nowrap transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C9A84C] ${
                      selectedCategory === 'all'
                        ? 'bg-[#C9A84C] text-[#070E1B] shadow-md'
                        : 'bg-[#162236] text-slate-300 border border-slate-700 hover:border-[#C9A84C]/50'
                    }`}
                  >
                    All Articles
                  </button>
                  {categories.map((cat) => (
                    <button
                      key={cat.id}
                      onClick={() => setSelectedCategory(cat.id)}
                      className={`px-3.5 py-2 rounded-xl text-xs font-semibold whitespace-nowrap transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C9A84C] ${
                        selectedCategory === cat.id
                          ? 'bg-[#C9A84C] text-[#070E1B] shadow-md'
                          : 'bg-[#162236] text-slate-300 border border-slate-700 hover:border-[#C9A84C]/50'
                      }`}
                    >
                      {cat.title}
                    </button>
                  ))}
                </div>

              </div>

            </motion.div>
          </div>
        </div>

        {/* Categories & Article Cards Grid */}
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 space-y-16">
          {filteredCategories.length === 0 ? (
            <div className="text-center py-16 bg-[#162236]/60 border border-[#C9A84C]/20 rounded-2xl p-8">
              <Search className="h-10 w-10 text-[#C9A84C] mx-auto mb-3 opacity-60" />
              <h3 className="text-xl font-bold font-playfair text-white mb-2">No Articles Found</h3>
              <p className="text-sm text-slate-300">Try adjusting your search query or switching category tabs.</p>
              <button
                onClick={() => { setSearchQuery(''); setSelectedCategory('all'); }}
                className="mt-4 px-4 py-2 bg-[#C9A84C]/20 text-[#E8D4A0] border border-[#C9A84C]/40 rounded-xl text-xs font-semibold hover:bg-[#C9A84C]/30 transition-all"
              >
                Clear Search Filter
              </button>
            </div>
          ) : (
            filteredCategories.map((category) => (
              <section key={category.id} className="space-y-6">
                <motion.div
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5 }}
                  className="border-b border-slate-800 pb-4"
                >
                  <h2 className="text-2xl md:text-3xl font-bold font-playfair text-[#E8D4A0] flex items-center gap-2.5">
                    <Sparkles className="w-5 h-5 text-[#C9A84C]" />
                    {category.title}
                  </h2>
                  <p className="text-slate-300 text-sm mt-1">{category.description}</p>
                </motion.div>

                <div className="grid sm:grid-cols-2 gap-6">
                  {category.posts.map((post) => (
                    <motion.div
                      key={post.slug}
                      initial={{ opacity: 0, y: 10 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.4 }}
                      className="group bg-[#162236]/90 border border-[#C9A84C]/25 hover:border-[#C9A84C]/60 hover:-translate-y-1.5 transition-all duration-300 rounded-2xl p-6 shadow-2xl backdrop-blur-xl flex flex-col justify-between"
                    >
                      <Link to={`/blog/${post.slug}`} className="block h-full flex flex-col justify-between space-y-4">
                        <div className="space-y-3">
                          <div className="flex items-center justify-between text-xs text-slate-400">
                            <div className="flex items-center gap-1.5">
                              <Calendar className="h-3.5 w-3.5 text-[#C9A84C]" />
                              <span>{post.date}</span>
                            </div>
                            <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-[#0D1B2A] text-[#E8D4A0] border border-[#C9A84C]/30">
                              {post.date === 'Available' ? 'Read Now' : 'Upcoming'}
                            </span>
                          </div>

                          <h3 className="text-xl font-bold font-playfair text-white group-hover:text-[#E8D4A0] transition-colors leading-snug">
                            {post.title}
                          </h3>

                          <p className="text-xs sm:text-sm text-slate-300 leading-relaxed line-clamp-3">
                            {post.excerpt}
                          </p>
                        </div>

                        <div className="pt-3 border-t border-slate-700/60 inline-flex items-center text-xs font-bold text-[#E8D4A0] group-hover:text-white transition-colors">
                          <span>Read Full Article</span>
                          <ArrowRight className="h-4 w-4 ml-1.5 text-[#C9A84C] group-hover:translate-x-1 transition-transform" />
                        </div>
                      </Link>
                    </motion.div>
                  ))}
                </div>
              </section>
            ))
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
}
